import { AxiosInstance } from 'axios';
import {
    SetupUserGoogleAuthInput,
    SetupUserPasswordAuthInput,
    SetUserEmailInput,
    ShutdownUserInput,
    UpdateUserEmailSettingsInput,
    UpdateUserInformationInput,
    UpdateUserPasswordAuthInput,
} from './inputs';
import { UserImageHashRef, UserRef } from './refs';
import { UserEmailSettingsResource, UserIdentityResource } from './resources';

export default class User {
    private basePath = '/api/user';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Fetches user identity
    getIdentity = async (id: ID): Promise<UserIdentityResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/identity`);

        return data;
    };

    // Patch generic user data
    update = async (id: ID, input: UpdateUserInformationInput): Promise<UserRef> => {
        const { data } = await this.http.patch(`${this.basePath}/${id}`, input);

        return data;
    };

    // Uploads and sets user image
    uploadAndSetImage = async (id: ID, image: File): Promise<UserImageHashRef> => {
        // Prepare form
        const form = new FormData();
        form.append('file', image);

        // Upload
        const { data } = await this.http.post(`${this.basePath}/${id}/image`, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return data;
    };

    // Removes current user image
    removeImage = async (id: ID): Promise<UserRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/image`);

        return data;
    };

    // Setup Google authentication
    setupGoogleAuth = async (id: ID, input: SetupUserGoogleAuthInput): Promise<UserRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/auth/google/setup`, input);

        return data;
    };

    // Disables Google authentication
    disableGoogleAuth = async (id: ID): Promise<UserRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/auth/google`);

        return data;
    };

    // Setup password authentication
    setupPasswordAuth = async (id: ID, input: SetupUserPasswordAuthInput): Promise<UserRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/auth/password/setup`, input);

        return data;
    };

    // Update password authentication
    updatePasswordAuth = async (id: ID, input: UpdateUserPasswordAuthInput): Promise<UserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/auth/password`, input);

        return data;
    };

    // Disables password authentication
    disablePasswordAuth = async (id: ID): Promise<UserRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/auth/password`);

        return data;
    };

    // Manually mark user as verified
    verify = async (id: ID): Promise<UserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/verification`);

        return data;
    };

    // Manually remove user verification status
    removeVerification = async (id: ID): Promise<UserRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/verification`);

        return data;
    };

    // Manually set user email
    setEmail = async (id: ID, input: SetUserEmailInput): Promise<UserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/email`, input);

        return data;
    };

    // Shutdown user
    shutdown = async (id: ID, input: ShutdownUserInput): Promise<UserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/shutdown`, input);

        return data;
    };

    // Fetch user email settings
    getEmailSettings = async (id: ID): Promise<UserEmailSettingsResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/email-settings`);

        return data;
    };

    // Update user email settings
    updateEmailSettings = async (id: ID, input: UpdateUserEmailSettingsInput): Promise<UserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/email-settings`, input);

        return data;
    };
}
